import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaChevronDown, FaQuestionCircle, FaSearch } from 'react-icons/fa';

gsap.registerPlugin(ScrollTrigger);

const FAQs = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const sectionRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(
      '.faq-item',
      { y: 30, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
        },
      }
    );
  }, []);

  const faqCategories = [
    {
      category: 'General Questions',
      icon: '📋',
      questions: [
        {
          question: 'What is AirCash Finance?',
          answer: 'AirCash Finance is a trusted Australian lender providing fast and simple personal loans. We offer quick online approval, transparent fees, and flexible repayment terms to help you meet your financial needs.',
        },
        {
          question: 'How do I apply for a loan?',
          answer: 'Applying is simple! Just fill out our online application form, which takes only a few minutes. Our consultants will then call you to complete a responsible lending assessment. Once approved, funds are deposited directly into your account.',
        },
        {
          question: 'How long does the approval process take?',
          answer: 'Most applications are assessed on the same day. Once approved, loan funds are typically deposited into your account within 24 hours.',
        },
        {
          question: 'What loan amounts are available?',
          answer: 'We offer personal loans ranging from $500 to $5,000, depending on your circumstances and our responsible lending assessment.',
        },
        {
          question: 'What are the repayment terms?',
          answer: 'We offer flexible repayment terms ranging from 6 to 24 months. You can choose a term that best suits your budget and financial situation.',
        },
      ],
    },
    {
      category: 'Application Process',
      icon: '📝',
      questions: [
        {
          question: 'What documents do I need to apply?',
          answer: 'You\'ll need proof of identity (driver\'s license or passport), proof of income (payslips or bank statements), and proof of address. Our consultant will guide you through the exact requirements during the application process.',
        },
        {
          question: 'Can I apply if I have bad credit?',
          answer: 'We assess each application individually based on responsible lending criteria. Having a less-than-perfect credit history doesn\'t automatically disqualify you. We consider your current financial situation and ability to repay the loan.',
        },
        {
          question: 'Is there a fee to apply?',
          answer: 'No, there are no application fees. All fees are clearly disclosed before you commit to the loan, and there are no hidden charges.',
        },
        {
          question: 'Can I apply online?',
          answer: 'Yes! Our entire application process is 100% online. You can apply from any device - computer, tablet, or smartphone - at any time that\'s convenient for you.',
        },
        {
          question: 'What happens after I submit my application?',
          answer: 'After you submit your application, one of our friendly consultants will call you to discuss your application and complete our responsible lending assessment. This helps us ensure the loan is suitable for your needs.',
        },
      ],
    },
    {
      category: 'Fees & Costs',
      icon: '💰',
      questions: [
        {
          question: 'What fees are involved?',
          answer: 'All fees are transparent and disclosed upfront. This includes establishment fees and any ongoing fees. There are no hidden charges - you\'ll know exactly what you\'ll pay before you commit to the loan.',
        },
        {
          question: 'What is the interest rate?',
          answer: 'Interest rates vary depending on the loan amount, term, and your individual circumstances. Our consultant will provide you with the exact rate and comparison rate during the application process.',
        },
        {
          question: 'Are there any early repayment fees?',
          answer: 'We encourage early repayment if you\'re able to. Please check with our consultant about any applicable fees for early repayment, as terms may vary.',
        },
        {
          question: 'Can I see a repayment schedule before applying?',
          answer: 'Yes! You can use our online calculator to estimate your repayments. Once you apply, our consultant will provide you with a detailed repayment schedule showing all payments and fees.',
        },
      ],
    },
    {
      category: 'Repayments',
      icon: '💳',
      questions: [
        {
          question: 'How do I make repayments?',
          answer: 'Repayments are automatically deducted from your nominated bank account on your chosen repayment date. This makes it convenient and ensures you never miss a payment.',
        },
        {
          question: 'Can I change my repayment date?',
          answer: 'Yes, you can request to change your repayment date. Please contact us to discuss your options, and we\'ll do our best to accommodate your needs.',
        },
        {
          question: 'What happens if I miss a payment?',
          answer: 'If you\'re having trouble making a payment, it\'s important to contact us as soon as possible. We\'re here to help and can discuss options such as payment plans or temporary hardship arrangements.',
        },
        {
          question: 'Can I pay off my loan early?',
          answer: 'Yes, you can pay off your loan early. Early repayment may help you save on interest. Please contact us to discuss any applicable fees or charges.',
        },
      ],
    },
    {
      category: 'Security & Privacy',
      icon: '🔒',
      questions: [
        {
          question: 'Is my personal information secure?',
          answer: 'Absolutely. We use bank-level encryption and security measures to protect your personal and financial information. Your data is handled in accordance with Australian privacy laws and our privacy policy.',
        },
        {
          question: 'Who will see my application?',
          answer: 'Your application is handled by our trained consultants and assessed according to responsible lending requirements. We only share information as required by law or with your consent.',
        },
        {
          question: 'How is my credit information used?',
          answer: 'We may check your credit report as part of our responsible lending assessment. This helps us determine your ability to repay the loan. All credit checks are conducted in accordance with Australian credit reporting laws.',
        },
      ],
    },
  ];

  const allQuestions = faqCategories.flatMap((category) =>
    category.questions.map((q) => ({ ...q, category: category.category }))
  );

  const filteredQuestions = searchQuery
    ? allQuestions.filter(
        (item) =>
          item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allQuestions;

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div ref={sectionRef} className="min-h-screen bg-white pt-32 pb-20">
      {/* Hero Section */}
      <section className="container mx-auto px-4 lg:px-8 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 bg-secondary/20 text-primary font-semibold px-4 py-2 rounded-full mb-6">
            <FaQuestionCircle className="text-secondary-600" />
            <span>Frequently Asked Questions</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-dark mb-6">
            Got Questions?{' '}
            <span className="text-gradient">We've Got Answers</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 mb-8">
            Find answers to common questions about our loans, application process, and more.
          </p>

          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search for questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-full border-2 border-gray-200 focus:border-primary focus:outline-none transition-colors"
            />
          </div>
        </motion.div>
      </section>

      {/* FAQs Section */}
      <section className="container mx-auto px-4 lg:px-8">
        {searchQuery ? (
          // Search Results
          <div className="max-w-4xl mx-auto">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-gray-600 mb-8 text-center"
            >
              Found {filteredQuestions.length} result{filteredQuestions.length !== 1 ? 's' : ''} for "{searchQuery}"
            </motion.p>
            <div className="space-y-4">
              {filteredQuestions.map((item, index) => {
                const globalIndex = allQuestions.findIndex((q) => q.question === item.question);
                return (
                  <FAQItem
                    key={index}
                    question={item.question}
                    answer={item.answer}
                    category={item.category}
                    index={globalIndex}
                    isOpen={openIndex === globalIndex}
                    onToggle={() => toggleFAQ(globalIndex)}
                  />
                );
              })}
            </div>
          </div>
        ) : (
          // Categorized FAQs
          <div className="max-w-4xl mx-auto space-y-12">
            {faqCategories.map((category, categoryIndex) => (
              <motion.div
                key={categoryIndex}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="faq-item"
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-3xl">{category.icon}</span>
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-dark">
                    {category.category}
                  </h2>
                </div>
                <div className="space-y-4">
                  {category.questions.map((item, questionIndex) => {
                    const globalIndex = faqCategories
                      .slice(0, categoryIndex)
                      .reduce((acc, cat) => acc + cat.questions.length, 0) + questionIndex;
                    return (
                      <FAQItem
                        key={questionIndex}
                        question={item.question}
                        answer={item.answer}
                        index={globalIndex}
                        isOpen={openIndex === globalIndex}
                        onToggle={() => toggleFAQ(globalIndex)}
                      />
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Still Have Questions CTA */}
        {!searchQuery && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-16 max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-br from-primary to-primary-700 rounded-3xl p-8 md:p-12 text-white text-center">
              <h3 className="font-display text-2xl md:text-3xl font-bold mb-4">
                Still Have Questions?
              </h3>
              <p className="text-white/80 mb-8 text-lg">
                Can't find what you're looking for? Our friendly team is here to help.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:0468098248"
                  className="bg-secondary hover:bg-secondary-400 text-primary-800 font-bold py-3 px-8 rounded-full transition-all duration-300 inline-flex items-center justify-center gap-2"
                >
                  Call Us: 0468 098 248
                </a>
                <a
                  href="/contact-us"
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border-2 border-white font-semibold py-3 px-8 rounded-full transition-all duration-300 inline-flex items-center justify-center gap-2"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </section>
    </div>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer, category, index, isOpen, onToggle }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.05 }}
      className="faq-item bg-white rounded-2xl border-2 border-gray-100 hover:border-primary/30 transition-all duration-300 shadow-sm hover:shadow-md overflow-hidden"
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 focus:outline-none focus:ring-2 focus:ring-primary/20 rounded-2xl"
      >
        <div className="flex-1">
          {category && (
            <span className="text-xs text-primary font-semibold mb-2 block">{category}</span>
          )}
          <h3 className="font-semibold text-lg text-dark pr-4">{question}</h3>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center"
        >
          <FaChevronDown className="text-primary" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-5 pt-0">
              <div className="pt-4 border-t border-gray-100">
                <p className="text-gray-600 leading-relaxed">{answer}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FAQs;

